/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { getAccountToken, fetchAccountMe, clearAccountSession, AccountAuthError, ACCOUNT_TOKEN_KEY } from '../lib/accountApi';
const AppStateContext = createContext(null);
// Shared across the whole app (marketing site + authenticated /app/* area) so
// the theme picker and company identity persist across that boundary — this
// is the Next.js root layout, which never unmounts on client navigation.
export function AppStateProvider({ children }) {
    const [brandTheme, setBrandTheme] = useState('indigo');
    const [companyProfile, setCompanyProfile] = useState({
        name: '',
        email: '',
        role: '',
        sector: '',
        location: '',
        trustScore: null,
        healthScore: null,
        globalReadinessScore: null,
        sustainabilityGrade: '',
    });
    // Gates /app/* (see RequireAuth in App.jsx). Starts false/true=loading
    // (matching server-rendered HTML, which has no access to localStorage)
    // and is corrected on the client right after mount, same pattern as the
    // Admin Console's isAuthed check in AdminConsole.jsx.
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    // Restores the session from the stored token. The token lives in
    // localStorage, so every tab of this browser shares it: opening a new tab
    // signs in automatically, and the listener below keeps already-open tabs
    // in step when another tab signs in or out.
    useEffect(() => {
        let cancelled = false;
        const syncSession = () => {
            const token = getAccountToken();
            if (!token) {
                setIsAuthenticated(false);
                setAuthLoading(false);
                return;
            }
            fetchAccountMe()
                .then((account) => {
                if (cancelled)
                    return;
                setCompanyProfile((prev) => ({ ...prev, name: account.companyName, sector: account.sector, email: account.email, role: account.role }));
                setIsAuthenticated(true);
            })
                .catch((err) => {
                if (cancelled)
                    return;
                if (err instanceof AccountAuthError)
                    clearAccountSession();
                setIsAuthenticated(false);
            })
                .finally(() => { if (!cancelled)
                setAuthLoading(false); });
        };
        syncSession();
        const onStorage = (event) => {
            // key === null means storage was cleared entirely.
            if (event.key === null || event.key === ACCOUNT_TOKEN_KEY)
                syncSession();
        };
        window.addEventListener('storage', onStorage);
        return () => {
            cancelled = true;
            window.removeEventListener('storage', onStorage);
        };
    }, []);
    const signOut = () => {
        clearAccountSession();
        setIsAuthenticated(false);
    };
    // Applies the brand-theme CSS class site-wide (public marketing pages
    // included, not just the authenticated /app/* shell which previously had
    // its own copy of this effect) — indigo-* Tailwind classes throughout the
    // app resolve through the theme-reactive --brand-* custom properties in
    // globals.css, so this one effect reskins every already-shipped section
    // with no per-component color changes needed.
    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('theme-emerald', 'theme-cobalt', 'theme-crimson', 'theme-amber');
        if (brandTheme !== 'indigo') {
            root.classList.add(`theme-${brandTheme}`);
        }
    }, [brandTheme]);
    return (<AppStateContext.Provider value={{ brandTheme, setBrandTheme, companyProfile, setCompanyProfile, isAuthenticated, setIsAuthenticated, authLoading, signOut }}>
      {children}
    </AppStateContext.Provider>);
}
export function useAppState() {
    const ctx = useContext(AppStateContext);
    if (!ctx)
        throw new Error('useAppState must be used within AppStateProvider');
    return ctx;
}
